import { useQuery } from '@apollo/client';
import { GetLatestOversightRatingsByDocumentIdsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';

export type OversightDocumentRatingLeaf = {
  Rating: number | null;
  TestDate: string | null;
  CreatedAt: string | null;
};

export type OversightRatingsByDocument = Map<
  string,
  OversightDocumentRatingLeaf
>;

const EMPTY_MAP: OversightRatingsByDocument = new Map();

const toTime = (value: string | null): number | null =>
  value === null ? null : new Date(value).getTime();

/**
 * Returns true when candidate `b` is a more recent oversight result than `a`,
 * using the SAME ordering the document page itself uses to pick its "latest"
 * oversight rating (getLatestOversightAssessmentDocumentAssessmentResultByDocumentId):
 * `TestDate` descending (nulls last), tie-broken by `CreatedAtTimestamp`
 * descending. Keeping this identical to the document-page query guarantees the
 * Policy Register and the document page never disagree on which rating is
 * current — so a newly assigned or re-dated rating surfaces in both places.
 */
const isMoreRecent = (
  a: OversightDocumentRatingLeaf,
  b: OversightDocumentRatingLeaf
): boolean => {
  const aTest = toTime(a.TestDate);
  const bTest = toTime(b.TestDate);
  if (aTest !== bTest) {
    // desc_nulls_last: a non-null TestDate always beats a null one.
    if (bTest === null) {
      return false;
    }
    if (aTest === null) {
      return true;
    }

    return bTest > aTest;
  }

  // Equal (or both-null) TestDate — tie-break on CreatedAtTimestamp desc.
  return (toTime(b.CreatedAt) ?? 0) > (toTime(a.CreatedAt) ?? 0);
};

/**
 * Fetches the latest oversight rating per document for the given document Ids,
 * keyed by DocumentId.
 *
 * "Latest" mirrors the document page exactly: the oversight result with the
 * newest `TestDate` (tie-broken by `CreatedAtTimestamp`), both read from the
 * result row itself. There is no completion gate — any oversight assessment
 * result counts, so the rating surfaces as soon as it is assigned.
 *
 * Like risks and obligations (and unlike controls), `document_oversight_result`
 * has no direct ParentDocumentId column — a finding can attach to multiple
 * documents via the `parents` array_relationship. `documentParent` is
 * server-filtered to ParentId ∈ $DocumentIds; the client fans the leaf out
 * across each matching parent. In practice the backend creates one leaf per
 * DocumentId, but the fan-out is correct either way.
 *
 * Skips the query when oversight viewing is disabled or no documents are
 * visible.
 */
export const useLatestOversightRatingsByDocuments = (params: {
  documentIds: string[];
  isEnabled: boolean;
}): {
  ratingsByDocument: OversightRatingsByDocument;
  loading: boolean;
} => {
  const { documentIds, isEnabled } = params;
  const skip = !isEnabled || documentIds.length === 0;

  const { data, loading } = useQuery(
    GetLatestOversightRatingsByDocumentIdsDocument,
    {
      skip,
      variables: { DocumentIds: documentIds },
      fetchPolicy: 'cache-and-network',
    }
  );

  const ratingsByDocument = useMemo<OversightRatingsByDocument>(() => {
    if (!data) {
      return EMPTY_MAP;
    }

    const latestByDocument: OversightRatingsByDocument = new Map();

    for (const row of data.document_oversight_result) {
      const leaf: OversightDocumentRatingLeaf = {
        Rating: row.Rating ?? null,
        TestDate: row.TestDate ?? null,
        CreatedAt: row.CreatedAtTimestamp ?? null,
      };
      for (const dp of row.documentParent) {
        const existing = latestByDocument.get(dp.ParentId);
        if (!existing || isMoreRecent(existing, leaf)) {
          latestByDocument.set(dp.ParentId, leaf);
        }
      }
    }

    return latestByDocument;
  }, [data]);

  return { ratingsByDocument, loading };
};
